/**
 * One ICS 221-CG as the numbered boxes it prints, each saved on its own.
 *
 * A documentation entry writes its boxes to the entry; a sheet a resource
 * check-out stamped writes them to that check-out's overlay, so the check-out
 * record itself is never rewritten. Every save then records an ICS_221 version
 * on the sheet's own chain, which is what the banner's "Last updated" reads.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PlusIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { FormVersioningBanner } from 'components/IAP/femaCards/FormVersioningBanner';
import {
  useUpdateIcs221Entry,
  useUpsertIcs221CheckoutOverlay,
  type Ics221Checkoff,
  type Ics221Entry,
  type Ics221EntryFieldsPatch,
  type Ics221EntryUpdate,
} from 'components/IAP/hooks/ics221EntryHooks';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import { useIcsFormInputs } from 'components/IAP/hooks/icsFormInputsHooks';
import {
  useFormVersionTip,
  useRecordFormVersionSave,
} from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { selectUser } from 'slices/commonSelectors';

import {
  ICS204_CONTENT,
  ICS204_TABLE,
} from '../AssignmentList/assignmentCardStyles';
import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { PrintedValue } from '../icsBoxPrimitives';
import SaveableField from '../SaveableField';
import {
  customUnitKey,
  clearSignOff,
  ics221CheckoffGroups,
  isoToLocalInput,
  localInputToIso,
  printedToLocalInput,
  signOffCheckoff,
  type Ics221CheckoffLine,
} from './ics221FormModel';

const NO_RESOURCE = 'none';

export type Ics221FormInstance =
  | { kind: 'entry'; entry: Ics221Entry }
  | {
      kind: 'checkout';
      resourceId: string;
      /** The edits already made on this check-out's sheet, if any. */
      overlay?: Ics221Entry;
    };

export type Ics221ResourceOption = { id: string; label: string };

const text = (value: unknown) => (typeof value === 'string' ? value : '');

/** An input that saves when it loses focus or on Enter, for Block 6's cells. */
function CommitInput({
  value,
  onCommit,
  label,
  placeholder,
  disabled,
}: {
  value: string;
  onCommit: (value: string) => void;
  label: string;
  placeholder?: string;
  disabled: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft !== null && draft !== value) onCommit(draft);
    setDraft(null);
  };
  return (
    <Input
      aria-label={label}
      placeholder={placeholder}
      disabled={disabled}
      value={draft ?? value}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') setDraft(null);
      }}
    />
  );
}

/** A Yes/No pair on the paper form, stated by the switch's answer. */
function YesNo({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Field orientation="horizontal" className="tw:gap-3">
      <Switch
        id={id}
        aria-label={label}
        disabled={disabled}
        checked={checked}
        onCheckedChange={onChange}
      />
      <FieldLabel htmlFor={id} className="tw:font-normal">
        {label}: {checked ? 'Yes' : 'No'}
      </FieldLabel>
    </Field>
  );
}

function SubBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
      <span className="tw:text-xs tw:text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export function Ics221Form({
  instance,
  title,
  resources,
  stampFirstVersion = false,
  embedded = false,
}: {
  instance: Ics221FormInstance;
  title: string;
  /** Inside a table row that already names the sheet: no card or title. */
  embedded?: boolean;
  resources: readonly Ics221ResourceOption[];
  /**
   * Records a version as soon as the sheet opens, for one just created, so its
   * banner names who started it before any box is filled in.
   */
  stampFirstVersion?: boolean;
}) {
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const { data: permissions } = useIncidentPermissions();
  const user = useSelector(selectUser);
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();
  const [newUnit, setNewUnit] = useState('');

  const instanceId =
    instance.kind === 'entry' ? instance.entry.id : instance.resourceId;
  const inputData =
    instance.kind === 'entry'
      ? { entryId: instance.entry.id }
      : { resourceId: instance.resourceId };
  const ownFields =
    instance.kind === 'entry'
      ? instance.entry.fields
      : (instance.overlay?.fields ?? {});

  const disabled =
    readOnlyForms || !(permissions?.['incident:editCards'] ?? false);
  const update = useUpdateIcs221Entry();
  const upsert = useUpsertIcs221CheckoutOverlay();
  const recordVersion = useRecordFormVersionSave(
    'ICS_221',
    effectivePeriodNumber,
    instanceId
  );
  const { data: tip } = useFormVersionTip(
    'ICS_221',
    effectivePeriodNumber,
    instanceId
  );
  const {
    data: inputs,
    isPending,
    isError,
  } = useIcsFormInputs('ICS_221_CG', {
    data: inputData,
    periodNumber: effectivePeriodNumber,
  });

  const stamped = useRef(false);
  useEffect(() => {
    if (!stampFirstVersion || stamped.current || readOnlyForms) return;
    stamped.current = true;
    recordVersion.mutate();
  }, [stampFirstVersion, readOnlyForms, recordVersion]);

  const signerName =
    user?.profile_settings?.userName?.trim() || user?.user_name?.trim() || '';

  /** One box's save, recorded as a version once it has landed. */
  const save = (
    fields: Ics221EntryFieldsPatch,
    entryPatch: Omit<Ics221EntryUpdate, 'fields'> = {}
  ) => {
    const onSuccess = () => {
      if (!readOnlyForms) recordVersion.mutate();
    };
    const onError = () => toast.error('The ICS 221 could not be saved.');
    if (instance.kind === 'entry') {
      update.mutate(
        {
          id: instance.entry.id,
          ...entryPatch,
          ...(Object.keys(fields).length ? { fields } : {}),
        },
        { onSuccess, onError }
      );
    } else {
      upsert.mutate(
        { resourceId: instance.resourceId, fields },
        { onSuccess, onError }
      );
    }
  };

  const saveCheckoff = (key: string, checkoff: Ics221Checkoff | null) =>
    save({ checkoffs: { [key]: checkoff } });

  const printed = inputs ?? {};
  const groups = ics221CheckoffGroups(printed);
  const allKeys = groups.flatMap((group) => group.lines.map((l) => l.key));

  const dateBox = (
    id: string,
    label: string,
    printedValue: unknown,
    field:
      | 'plannedReleaseDatetime'
      | 'estimatedDepartureDatetime'
      | 'actualReleaseDatetime'
      | 'estimatedArrivalDatetime'
  ) => (
    <SaveableField
      id={`ics221-${instanceId}-${id}`}
      label={label}
      type="datetime-local"
      disabled={disabled}
      value={
        ownFields[field]
          ? isoToLocalInput(ownFields[field])
          : printedToLocalInput(printedValue)
      }
      onSave={(value) => save({ [field]: localInputToIso(value) })}
    />
  );

  const textBox = (
    id: string,
    label: string,
    value: unknown,
    onSave: (value: string) => void,
    options: { multiline?: boolean; hideLabel?: boolean } = {}
  ) => (
    <SaveableField
      id={`ics221-${instanceId}-${id}`}
      label={label}
      hideLabel={options.hideLabel}
      multiline={options.multiline}
      photos={false}
      disabled={disabled}
      value={text(value)}
      onSave={onSave}
    />
  );

  const checkoffRow = (line: Ics221CheckoffLine) => {
    const { checkoff } = line;
    const signedAt = checkoff.datetime
      ? new Date(checkoff.datetime).toLocaleString()
      : '';
    return (
      <tr key={line.key} data-testid={`ics221-checkoff-${line.key}`}>
        <td className="tw:px-2 tw:py-1 tw:align-middle">
          <Checkbox
            aria-label={`${line.label} must sign off`}
            disabled={disabled}
            checked={Boolean(checkoff.checked)}
            onCheckedChange={(checked) =>
              saveCheckoff(line.key, { ...checkoff, checked: Boolean(checked) })
            }
          />
        </td>
        <td className="tw:px-2 tw:py-1 tw:align-middle tw:whitespace-nowrap">
          {line.label}
        </td>
        <td className="tw:px-2 tw:py-1">
          <CommitInput
            label={`${line.label} remarks`}
            disabled={disabled}
            value={checkoff.remarks ?? ''}
            onCommit={(remarks) =>
              saveCheckoff(line.key, { ...checkoff, remarks })
            }
          />
        </td>
        <td className="tw:px-2 tw:py-1">
          <CommitInput
            label={`${line.label} name`}
            placeholder={line.defaultName}
            disabled={disabled}
            value={checkoff.name ?? ''}
            onCommit={(name) => saveCheckoff(line.key, { ...checkoff, name })}
          />
        </td>
        <td className="tw:px-2 tw:py-1 tw:align-middle">
          <div className="tw:flex tw:items-center tw:gap-2">
            {signedAt ? (
              <>
                <span
                  className="tw:text-xs tw:whitespace-nowrap"
                  data-testid={`ics221-checkoff-${line.key}-signed`}
                >
                  Signed {signedAt}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() => saveCheckoff(line.key, clearSignOff(checkoff))}
                >
                  Undo
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                data-testid={`ics221-checkoff-${line.key}-sign`}
                onClick={() =>
                  saveCheckoff(
                    line.key,
                    signOffCheckoff(checkoff, {
                      name: signerName,
                      userGuid: user?.user_guid,
                    })
                  )
                }
              >
                Sign off
              </Button>
            )}
            {line.custom ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove ${line.label}`}
                disabled={disabled}
                onClick={() => saveCheckoff(line.key, null)}
              >
                <XIcon />
              </Button>
            ) : null}
          </div>
        </td>
      </tr>
    );
  };

  const addUnit = () => {
    const label = newUnit.trim();
    if (!label) return;
    saveCheckoff(customUnitKey(label, allKeys), {
      checked: true,
      position: label,
    });
    setNewUnit('');
  };

  const signer = tip?.signatures?.[0];
  const preparedBy = signer
    ? {
        name: signer.name,
        position: signer.positionTitle ?? '',
        signature: 'Signed in PRATUS',
        at: new Date(signer.signedAt).toLocaleString(),
      }
    : {
        name: text(printed.preparedByName),
        position: text(printed.preparedByPosition),
        signature: text(printed.preparedBySignature),
        at: text(printed.preparedByName) ? text(printed.preparedDateTime) : '',
      };

  const exportButton = (
    <FormExportButton
      formname="ICS_221_CG"
      label="Preview ICS-221"
      disabled={isExporting}
      onExport={(format) =>
        void exportAsForm({
          formname: 'ICS_221_CG',
          data: inputData,
          mapSketch: null,
          format,
          periodNumber: effectivePeriodNumber,
        })
      }
    />
  );

  const content = (
    <>
      <FormVersioningBanner baseId="ICS_221" formInstanceId={instanceId} />

      {isPending ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Loading the ICS 221…
        </p>
      ) : isError ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          This ICS 221 could not be read. Try the preview to see the error.
        </p>
      ) : (
        <>
          <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
            <Ics204CardSection
              boxLabel="1. Incident Name:"
              testId="ics221-section-incident-name"
              className="tw:mt-0 tw:h-full tw:min-w-0"
            >
              <PrintedValue
                value={text(printed.incidentName)}
                testId="ics221-field-incident-name"
              />
            </Ics204CardSection>
            <Ics204CardSection
              boxLabel="2. Incident Location:"
              testId="ics221-section-incident-location"
              className="tw:mt-0 tw:h-full tw:min-w-0"
            >
              <PrintedValue
                value={text(printed.incidentLocation)}
                testId="ics221-field-incident-location"
              />
            </Ics204CardSection>
            <Ics204CardSection
              boxLabel="3. Planned Release Date / Time:"
              testId="ics221-section-planned-release"
              className="tw:mt-0 tw:h-full tw:min-w-0"
            >
              {dateBox(
                'planned-release',
                'Planned release',
                `${text(printed.plannedReleaseDate)} ${text(
                  printed.plannedReleaseTime
                )}`,
                'plannedReleaseDatetime'
              )}
            </Ics204CardSection>
          </div>

          <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
            <Ics204CardSection
              boxLabel="4. Resource / Personnel Released:"
              testId="ics221-section-resource-released"
              className="tw:mt-0 tw:h-full tw:min-w-0"
            >
              <div className="tw:flex tw:flex-col tw:gap-3">
                {instance.kind === 'entry' ? (
                  <SubBox label="Incident resource (optional)">
                    <Select
                      items={[
                        {
                          value: NO_RESOURCE,
                          label: 'None — describe the resource',
                        },
                        ...resources.map((resource) => ({
                          value: resource.id,
                          label: resource.label,
                        })),
                      ]}
                      value={instance.entry.resourceInIncidentId ?? NO_RESOURCE}
                      disabled={disabled}
                      onValueChange={(next) =>
                        save(
                          {},
                          {
                            resourceInIncidentId:
                              next && next !== NO_RESOURCE ? next : null,
                          }
                        )
                      }
                    >
                      <SelectTrigger aria-label="Incident resource">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark">
                        <SelectItem value={NO_RESOURCE}>
                          None — describe the resource
                        </SelectItem>
                        {resources.map((resource) => (
                          <SelectItem key={resource.id} value={resource.id}>
                            {resource.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SubBox>
                ) : null}
                {textBox(
                  'resource-released',
                  'Resource / personnel released',
                  printed.resourcePersonnelReleased,
                  (value) => save({ resourcePersonnelReleased: value }),
                  { hideLabel: instance.kind === 'checkout' }
                )}
                {instance.kind === 'entry' &&
                !instance.entry.resourceInIncidentId
                  ? textBox(
                      'resource-kind',
                      'Resource kind',
                      instance.entry.resourceKind,
                      (value) => save({}, { resourceKind: value })
                    )
                  : null}
              </div>
            </Ics204CardSection>
            <Ics204CardSection
              boxLabel="5. Order Request Number:"
              testId="ics221-section-order-request"
              className="tw:mt-0 tw:h-full tw:min-w-0"
            >
              {textBox(
                'order-request',
                'Order request number',
                printed.orderRequestNumber,
                (value) => save({ orderRequestNumber: value }),
                { hideLabel: true }
              )}
            </Ics204CardSection>
          </div>

          <Ics204CardSection
            boxLabel="6. Release of Resource or Personnel:"
            testId="ics221-section-release"
            className="tw:mt-0"
          >
            <p className="tw:m-0 tw:mb-2 tw:text-xs tw:text-muted-foreground">
              Check each unit the resource must check out with. The resource is
              not released until every checked unit has signed off.
            </p>
            <div className="tw:overflow-x-auto">
              <table className={ICS204_TABLE}>
                <thead>
                  <tr>
                    <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                      Required
                    </th>
                    <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                      Unit / Manager
                    </th>
                    <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                      Remarks
                    </th>
                    <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                      Name
                    </th>
                    <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                      Signature
                    </th>
                  </tr>
                </thead>
                {groups.map((group) => (
                  <tbody key={group.section}>
                    <tr>
                      <th
                        colSpan={5}
                        scope="rowgroup"
                        className="tw:px-2 tw:pt-3 tw:pb-1 tw:text-left tw:text-xs tw:font-medium tw:uppercase tw:text-muted-foreground"
                      >
                        {group.section}
                      </th>
                    </tr>
                    {group.lines.map(checkoffRow)}
                    {group.section === 'Other Staff' && !disabled ? (
                      <tr>
                        <td />
                        <td colSpan={4} className="tw:px-2 tw:py-1">
                          <div className="tw:flex tw:max-w-md tw:gap-2">
                            <Input
                              aria-label="Other staff unit"
                              placeholder="Add a unit, e.g. Medical Unit"
                              value={newUnit}
                              onChange={(event) =>
                                setNewUnit(event.target.value)
                              }
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') addUnit();
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!newUnit.trim()}
                              data-testid="ics221-add-unit"
                              onClick={addUnit}
                            >
                              <PlusIcon />
                              Add unit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                ))}
              </table>
            </div>
          </Ics204CardSection>

          <Ics204CardSection
            boxLabel="7. Remarks:"
            testId="ics221-section-remarks"
            className="tw:mt-0"
          >
            {textBox(
              'remarks',
              'Remarks',
              printed.remarks,
              (value) => save({ remarks: value }),
              { multiline: true, hideLabel: true }
            )}
          </Ics204CardSection>

          <Ics204CardSection
            boxLabel="8. Travel Information:"
            testId="ics221-section-travel"
            className="tw:mt-0"
          >
            <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-2 tw:xl:grid-cols-3">
              <YesNo
                id={`ics221-${instanceId}-overnight`}
                label="Room overnight"
                checked={Boolean(printed.overnightRequired)}
                disabled={disabled}
                onChange={(checked) => save({ overnightRequired: checked })}
              />
              {dateBox(
                'departure',
                'Estimated time of departure',
                printed.estimatedTimeOfDeparture,
                'estimatedDepartureDatetime'
              )}
              {dateBox(
                'actual-release',
                'Actual release date / time',
                printed.actualReleaseDateTime,
                'actualReleaseDatetime'
              )}
              {textBox(
                'destination',
                'Destination',
                printed.destination,
                (value) => save({ destination: value })
              )}
              {dateBox(
                'arrival',
                'Estimated time of arrival',
                printed.estimatedTimeOfArrival,
                'estimatedArrivalDatetime'
              )}
              {textBox(
                'travel-method',
                'Travel method',
                printed.travelMethod,
                (value) => save({ travelMethod: value })
              )}
              {textBox(
                'contact',
                'Contact information while traveling',
                printed.contactInformationWhileTraveling,
                (value) => save({ contactWhileTraveling: value })
              )}
              <div className="tw:flex tw:flex-col tw:gap-3">
                <YesNo
                  id={`ics221-${instanceId}-manifest`}
                  label="Manifest"
                  checked={Boolean(printed.manifestRequired)}
                  disabled={disabled}
                  onChange={(checked) => save({ manifestRequired: checked })}
                />
                {printed.manifestRequired
                  ? textBox(
                      'manifest-number',
                      'Manifest #',
                      printed.manifestNumber,
                      (value) => save({ manifestNumber: value })
                    )
                  : null}
              </div>
              <div className="tw:flex tw:flex-col tw:gap-3">
                <YesNo
                  id={`ics221-${instanceId}-notified`}
                  label="Unit / agency notified"
                  checked={Boolean(printed.unitAgencyNotified)}
                  disabled={disabled}
                  onChange={(checked) => save({ unitAgencyNotified: checked })}
                />
                {printed.unitAgencyNotified
                  ? textBox(
                      'notified-name',
                      'Who was notified, and when',
                      ownFields.unitAgencyNotifiedName ??
                        printed.unitAgencyNotifiedText,
                      (value) => save({ unitAgencyNotifiedName: value })
                    )
                  : null}
              </div>
            </div>
          </Ics204CardSection>

          <Ics204CardSection
            boxLabel="9. Prepared by:"
            testId="ics221-section-prepared-by"
            className="tw:mt-0"
          >
            <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2 tw:lg:grid-cols-4">
              <SubBox label="Name">
                <PrintedValue
                  value={preparedBy.name}
                  testId="ics221-field-prepared-name"
                />
              </SubBox>
              <SubBox label="Position / Title">
                <PrintedValue
                  value={preparedBy.position}
                  testId="ics221-field-prepared-position"
                />
              </SubBox>
              <SubBox label="Signature">
                <PrintedValue
                  value={preparedBy.signature}
                  testId="ics221-field-prepared-signature"
                />
              </SubBox>
              <SubBox label="Date / Time">
                <PrintedValue
                  value={preparedBy.at}
                  testId="ics221-field-prepared-datetime"
                />
              </SubBox>
            </div>
            <p className="tw:m-0 tw:mt-2 tw:text-xs tw:text-muted-foreground">
              Sign the sheet from the version bar above to fill this box.
            </p>
          </Ics204CardSection>
        </>
      )}
    </>
  );

  if (embedded) {
    return (
      <div
        className={`dark tw:flex tw:min-w-0 tw:flex-col tw:gap-3 tw:text-foreground ${ICS204_CONTENT}`}
        data-testid="ics221-form"
      >
        <div className="tw:flex tw:justify-end">{exportButton}</div>
        {content}
      </div>
    );
  }

  return (
    <Card
      className="dark tw:bg-background tw:text-foreground"
      data-testid="ics221-form"
    >
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardAction>{exportButton}</CardAction>
      </CardHeader>
      <CardContent className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
        {content}
      </CardContent>
    </Card>
  );
}

export default Ics221Form;
